export type SSEEventType =
  | 'ACCOUNT_UPDATE'
  | 'POSITION_CHANGE'
  | 'BALANCE_CHANGE'
  | 'SYNC_COMPLETE'
  | 'ERROR';

export interface SSEEvent {
  type: SSEEventType;
  data: any;
  timestamp: string;
  correlationId?: string;
}

type EventHandler = (event: SSEEvent) => void;

class SSEService {
  private abortController: AbortController | null = null;
  private handlers: Map<SSEEventType, EventHandler[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnected = false;
  private authToken: string | null = null;
  private connectionStatusListeners: ((isConnected: boolean) => void)[] = [];

  setAuthToken(token: string): void {
    this.authToken = token;
  }

  connect(): void {
    if (this.abortController) {
      this.disconnect();
    }

    if (!this.authToken) {
      console.error('No auth token available for SSE connection');
      return;
    }

    try {
      this.abortController = new AbortController();
      this.setupStreamConnection();
    } catch (error) {
      console.error('Failed to connect to SSE stream:', error);
      this.scheduleReconnect();
    }
  }

  private async setupStreamConnection(): Promise<void> {
    if (!this.abortController || !this.authToken) return;

    try {
      const response = await fetch('/api/v1/events/stream', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.authToken}`,
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `SSE connection failed: ${response.status} ${response.statusText}`,
        );
      }

      console.log('SSE connection established');
      this.setConnectionStatus(true);
      this.reconnectAttempts = 0;

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No readable stream available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('SSE stream ended');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const eventData = line.slice(6); // Remove 'data: ' prefix
              const sseEvent: SSEEvent = JSON.parse(eventData);
              this.handleEvent(sseEvent);
            } catch (error) {
              console.error('Failed to parse SSE event:', error);
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('SSE connection aborted');
        return;
      }

      console.error('SSE connection error:', error);
      this.setConnectionStatus(false);
      this.scheduleReconnect();
    }
  }

  private handleEvent(event: SSEEvent): void {
    const handlers = this.handlers.get(event.type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          console.error(`Error in SSE handler for ${event.type}:`, error);
        }
      });
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max SSE reconnection attempts reached');
      this.setConnectionStatus(false);
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    setTimeout(() => {
      console.log(
        `Attempting SSE reconnection (${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
      );
      this.connect();
    }, delay);
  }

  disconnect(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.setConnectionStatus(false);
  }

  on(eventType: SSEEventType, handler: EventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, []);
    }

    this.handlers.get(eventType)!.push(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.handlers.get(eventType);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }

  off(eventType: SSEEventType, handler: EventHandler): void {
    const handlers = this.handlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  // Convenience methods for common event types
  onAccountUpdate(handler: (data: any) => void): () => void {
    return this.on('ACCOUNT_UPDATE', (event) => handler(event.data));
  }

  onPositionChange(handler: (data: any) => void): () => void {
    return this.on('POSITION_CHANGE', (event) => handler(event.data));
  }

  onBalanceChange(handler: (data: any) => void): () => void {
    return this.on('BALANCE_CHANGE', (event) => handler(event.data));
  }

  onSyncComplete(handler: (data: any) => void): () => void {
    return this.on('SYNC_COMPLETE', (event) => handler(event.data));
  }

  onError(handler: (data: any) => void): () => void {
    return this.on('ERROR', (event) => handler(event.data));
  }

  private setConnectionStatus(status: boolean) {
    this.isConnected = status;
    this.connectionStatusListeners.forEach((cb) => cb(status));
  }

  onConnectionStatusChange(cb: (isConnected: boolean) => void): () => void {
    this.connectionStatusListeners.push(cb);
    // Immediately call with current status
    cb(this.isConnected);
    return () => {
      this.connectionStatusListeners = this.connectionStatusListeners.filter(
        (fn) => fn !== cb,
      );
    };
  }
}

export const sseService = new SSEService();
export default sseService;
